import './CompareMetrics.scss';
import { Metric, Metrics, MetricTotalsByDate } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { MdClose } from 'react-icons/md';
import { XAxis, Tooltip, CartesianGrid, ComposedChart } from 'recharts';
import * as R from 'ramda';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { ReactNode, useState, useMemo, useContext, useCallback, useEffect } from 'react';
import {
  getCurrencySymbol,
  getMetricFormatter,
  MetricFormatter
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { CompanyFiscalCal, DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Placement, Size, StretchMode } from '@Components/designConstants';
import { DateRange, DATE_GROUPING_OPTIONS, formatDateLabel } from '@src/utils/date-utils';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { ChartTooltipV2, PayloadItem } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
  X_AXIS_PADDING
} from '@Components/Charts/ChartConstants';
import { ChartContainer } from '@Components/ChartContainer';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Spinner } from '@Components/Spinner/Spinner';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { RELATIVE_FORMATTER } from '../cross-channel-formatters';
import {
  joinPrimaryAndComparisonData,
  getPrettyMetricName,
  getJoinedDataWithPercentChange,
  JoinedData
} from '../cross-channel-utils';
import {
  DESCRIPTIVE_METRICS,
  METRIC_OPTIONS,
  METRIC_TO_PRETTY_NAME
} from '../cross-channel-constants';
import { totalsByDateIncrement } from '../cross-channel-chart-utils';
import { getCompareMetricsCartesianComponents } from './cartesianComponents';
import { CrossChannelContext } from '../cross-channel-context';
import { DateRangeComparisonTitle } from './DateRangeComparisonTitle';

const MAX_COMPARISON_METRICS = 3;

/**
 * The wider the selected date range, the coarser the allowed date-grouping options
 * (e.g. a single day can't be grouped by Year, but a full year of data can).
 */
const getDateGroupingsAllowed = (maxDateRange: number): number => {
  if (maxDateRange >= 365) return 4;
  if (maxDateRange >= 90) return 3;
  if (maxDateRange >= 30) return 2;
  if (maxDateRange >= 7) return 1;
  return 0;
};

export interface CompareMetricOption {
  fetchGetter?: (metrics: Metrics) => number;
  isCustom?: boolean;
  label: string;
  value: Metric;
}

interface CompareMetricsProps {
  // Overrides the company-currency formatter (e.g. to format custom metrics).
  formatter?: MetricFormatter;
  data: MetricTotalsByDate[];
  otherData: MetricTotalsByDate[];
  defaultMetrics?: Metric[];
  defaultIncludeOtherDates: boolean;
  pngDownloadLabel?: string;
  pngFileIdentifiers?: string[];
  dates?: DateRange;
  otherDates?: DateRange;
  defaultDateGroupingKey?: DateGroupingKey;
  dateIncrementOptions?: DateGroupingKey[];
  includeFooter?: boolean;
  metricOptions?: CompareMetricOption[];
  // Points already bucketed by the server for `groupingKey`; used instead of summing daily `data`.
  serverGroupedData?: {
    groupingKey: DateGroupingKey;
    data: MetricTotalsByDate[];
    otherData: MetricTotalsByDate[];
  };
  onGroupingKeyChange?: (groupingKey: DateGroupingKey) => void;
  // When the server-grouped fetch failed or cannot run, fall back to summing daily `data`.
  serverGroupedUnavailable?: boolean;
}

interface CompareMetricsTooltipEntry {
  dataKey?: string;
  name?: string;
  value?: string | number;
  stroke?: string;
  payload?: { date?: string; date_other?: string };
}

interface CompareMetricsTooltipProps {
  includeOtherDateRange: boolean;
  showPercentChange: boolean;
  fiscalCal: CompanyFiscalCal;
  groupingKey: DateGroupingKey;
  metricFormatter: (metric: string) => (value: number) => string;
  // recharts injects this when it renders the tooltip content
  payload?: CompareMetricsTooltipEntry[];
}

const CompareMetricsTooltip = ({
  includeOtherDateRange,
  showPercentChange,
  fiscalCal,
  groupingKey,
  metricFormatter,
  payload
}: CompareMetricsTooltipProps) => {
  const fullPayload = payload?.[0]?.payload || {};

  // Sort by magnitude on the raw numeric value, before formatting turns it into a display
  // string ("$1,234", "12.3%") that Number() can no longer parse back out.
  const byDescendingMagnitude = (a: { value: number }, b: { value: number }): number =>
    Math.abs(b.value) - Math.abs(a.value);

  let headerLabel: string; // Label for the date
  let items: PayloadItem[]; // Metrics
  let comparisonHeaderLabel = ''; // Label for the comparison date
  let comparisonItems: PayloadItem[] | undefined; // Comparison metrics

  // If including other date range, we have to split out the
  // payload into the two date ranges.
  if (includeOtherDateRange) {
    headerLabel = formatDateLabel(fullPayload.date || '', fiscalCal, groupingKey, true);
    comparisonHeaderLabel = formatDateLabel(
      fullPayload.date_other || '',
      fiscalCal,
      groupingKey,
      true
    );

    items = (
      payload
        ?.filter(item => R.pathOr('', ['dataKey'], item).includes('metrics.'))
        .map(item => {
          const { dataKey, name, value, stroke } = item;
          let formattedValue: string;
          if (showPercentChange) {
            formattedValue = value === '-' ? '-' : RELATIVE_FORMATTER(value as number);
          } else {
            formattedValue = metricFormatter((dataKey || '').replace('metrics.', ''))(
              value as number
            );
          }
          return {
            label: name as string,
            value: formattedValue,
            color: stroke,
            sortValue: Number(value)
          };
        }) || []
    )
      .sort((a, b) => byDescendingMagnitude({ value: a.sortValue }, { value: b.sortValue }))
      .map(({ sortValue: _sortValue, ...item }) => item);

    comparisonItems = (
      payload
        ?.filter(item => R.pathOr('', ['dataKey'], item).includes('metrics_other.'))
        .map(item => {
          const { dataKey, name, value, stroke } = item;
          const formattedValue = metricFormatter((dataKey || '').replace('metrics_other.', ''))(
            value as number
          );
          return {
            label: name as string,
            value: formattedValue,
            color: stroke,
            sortValue: Number(value)
          };
        }) || []
    )
      .sort((a, b) => byDescendingMagnitude({ value: a.sortValue }, { value: b.sortValue }))
      .map(({ sortValue: _sortValue, ...item }) => item);

    if (showPercentChange) {
      headerLabel = `${fullPayload.date} vs ${fullPayload.date_other}`;
      comparisonHeaderLabel = ''; // No need to show the comparison date in this case
    }
  } else {
    headerLabel = formatDateLabel(fullPayload.date || '', fiscalCal, groupingKey, true);
    items = (
      payload?.map(item => {
        const { name, value, stroke, dataKey } = item;
        const formattedValue = metricFormatter((dataKey || '').replace('metrics.', ''))(
          value as number
        );
        return {
          label: name as string,
          value: formattedValue,
          color: stroke,
          sortValue: Number(value)
        };
      }) || []
    )
      .sort((a, b) => byDescendingMagnitude({ value: a.sortValue }, { value: b.sortValue }))
      .map(({ sortValue: _sortValue, ...item }) => item);
  }

  return (
    <ChartTooltipV2
      headers={[headerLabel, comparisonHeaderLabel]}
      items={items}
      comparisonItems={comparisonItems}
    />
  );
};

export const CompareMetrics = ({
  data,
  otherData,
  defaultMetrics,
  defaultIncludeOtherDates,
  pngDownloadLabel,
  pngFileIdentifiers = [],
  dates: datesInput,
  otherDates: otherDatesInput,
  defaultDateGroupingKey = 'Day',
  dateIncrementOptions,
  includeFooter = false,
  metricOptions = METRIC_OPTIONS,
  formatter,
  serverGroupedData,
  onGroupingKeyChange,
  serverGroupedUnavailable = false
}: CompareMetricsProps) => {
  const [groupingKey, setGroupingKey] = useState(defaultDateGroupingKey);
  const [primaryMetric, setPrimaryMetric] = useState<Metric>(
    defaultMetrics && defaultMetrics[0] ? defaultMetrics[0] : Metric.SPEND
  );
  const [comparisonMetrics, setComparisonMetrics] = useState<Metric[]>(
    defaultMetrics && defaultMetrics.length > 1
      ? R.filter(metric => !R.isNil(metric), defaultMetrics.slice(1))
      : [Metric.IMPRESSIONS]
  );
  const [graphView, setGraphView] = useState(true);
  const [includeOtherDateRange, setIncludeOtherDateRange] =
    useState<boolean>(defaultIncludeOtherDates);
  const [showPercentChange, setShowPercentChange] = useState(false);
  const [hoveredLegendItem, setHoveredLegendItem] = useState<string>('');
  const [focusedMetric, setFocusedMetric] = useState<string>('');
  const { fiscal_cal: fiscalCal, currency_code: currencyCode } = useCompanyInfo();
  const metricFormatter = useMemo(
    () => formatter ?? getMetricFormatter(currencyCode),
    [formatter, currencyCode]
  );
  const currSymbol = useMemo(() => getCurrencySymbol(currencyCode), [currencyCode]);

  const {
    dates: contextDates,
    otherDates: contextOtherDates,
    platformAsSourceOfData,
    dataInfo
  } = useContext(CrossChannelContext);
  const dates = R.defaultTo(contextDates, datesInput);
  const otherDates = R.defaultTo(contextOtherDates, otherDatesInput);

  const metricsToPrettyNameMap = useMemo(() => {
    const prettyNameMap = {};
    for (const metric of metricOptions) {
      prettyNameMap[metric.value] = metric.label;
    }
    return prettyNameMap;
  }, [metricOptions]);

  const footerContent = useMemo(() => {
    const metricPrettyNamesByGroup = [...comparisonMetrics, primaryMetric].reduce(
      (acc, cv) => {
        const isDescriptive = DESCRIPTIVE_METRICS.includes(cv);
        if (platformAsSourceOfData) {
          acc.platformMetricsInUse.push(metricsToPrettyNameMap[cv]);
        } else if (isDescriptive) {
          acc.platformMetricsInUse.push(metricsToPrettyNameMap[cv]);
        } else {
          acc.analyticMetricsInUse.push(metricsToPrettyNameMap[cv]);
        }

        if (isDescriptive) {
          acc.descriptiveMetricsInUse.push(metricsToPrettyNameMap[cv]);
        }
        return acc;
      },
      {
        descriptiveMetricsInUse: [] as string[],
        platformMetricsInUse: [] as string[],
        analyticMetricsInUse: [] as string[]
      }
    );
    const formattedFooterArr: string[] = [];

    if (!R.isEmpty(metricPrettyNamesByGroup.descriptiveMetricsInUse)) {
      formattedFooterArr.push(
        `KPI picker doesn't apply to ${metricPrettyNamesByGroup.descriptiveMetricsInUse.join(', ')}`
      );
    }
    if (!R.isEmpty(metricPrettyNamesByGroup.platformMetricsInUse)) {
      formattedFooterArr.push(
        `Source for ${metricPrettyNamesByGroup.platformMetricsInUse.join(', ')}: Platform`
      );
    }
    if (!R.isEmpty(metricPrettyNamesByGroup.analyticMetricsInUse)) {
      formattedFooterArr.push(
        `Source for ${metricPrettyNamesByGroup.analyticMetricsInUse.join(', ')}: Source of Truth`
      );
    }

    return formattedFooterArr;
  }, [comparisonMetrics, metricsToPrettyNameMap, platformAsSourceOfData, primaryMetric]);

  const dateGroupingOptions = useMemo(() => {
    const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
    const diffDays = Math.round(
      Math.abs((new Date(dates.start).getTime() - new Date(dates.end).getTime()) / oneDay)
    );
    const diffOtherDays = R.isNil(otherDates)
      ? 0
      : Math.round(
          Math.abs(
            (new Date(otherDates.start).getTime() - new Date(otherDates.end).getTime()) / oneDay
          )
        );
    const maxDateRange = R.max(diffDays, diffOtherDays);
    const dateGroupingsAllowed = getDateGroupingsAllowed(maxDateRange);
    const filteredDateGroupOptions: {
      label: string;
      value: 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year';
      className?: string;
    }[] = [];
    for (let i = 0; i < DATE_GROUPING_OPTIONS.length; i++) {
      filteredDateGroupOptions.push({
        label: DATE_GROUPING_OPTIONS[i].label,
        value: DATE_GROUPING_OPTIONS[i].value,
        className: dateGroupingsAllowed < i ? 'disabled' : ''
      });
    }
    if (!R.isNil(dateIncrementOptions)) {
      const newOptions = R.filter(
        option => dateIncrementOptions.includes(option.value),
        filteredDateGroupOptions
      );
      newOptions[0].className = '';
      return newOptions;
    }
    return filteredDateGroupOptions;
  }, [dateIncrementOptions, dates, otherDates]);

  useEffect(() => {
    if (!dateGroupingOptions.map(option => option.value).includes(groupingKey)) {
      setGroupingKey(dateGroupingOptions[0].value);
    }
  }, [dateGroupingOptions, groupingKey]);

  useEffect(() => {
    onGroupingKeyChange?.(groupingKey);
  }, [groupingKey, onGroupingKeyChange]);

  // A parent that fetches server-grouped points: wait for them instead of showing summed ratios.
  const awaitingServerData =
    Boolean(onGroupingKeyChange) &&
    groupingKey !== 'Day' &&
    serverGroupedData?.groupingKey !== groupingKey &&
    !serverGroupedUnavailable;

  const { dataGroupedByDateIncrement, otherDataGroupedByDateIncrement } = useMemo(() => {
    if (groupingKey !== 'Day' && serverGroupedData?.groupingKey === groupingKey) {
      return {
        dataGroupedByDateIncrement: serverGroupedData.data,
        otherDataGroupedByDateIncrement: serverGroupedData.otherData
      };
    }
    const groupedData = totalsByDateIncrement(data, groupingKey, metricOptions, fiscalCal);
    const groupedOtherData = totalsByDateIncrement(
      otherData,
      groupingKey,
      metricOptions,
      fiscalCal
    );
    return {
      dataGroupedByDateIncrement: groupedData,
      otherDataGroupedByDateIncrement: groupedOtherData
    };
  }, [data, otherData, groupingKey, metricOptions, fiscalCal, serverGroupedData]);

  const joinedData = useMemo(() => {
    const joined = joinPrimaryAndComparisonData({
      primaryData: dataGroupedByDateIncrement,
      comparisonData: otherDataGroupedByDateIncrement,
      includeAllComparisonDates: includeOtherDateRange
    });

    return showPercentChange ? getJoinedDataWithPercentChange(joined) : joined;
  }, [
    dataGroupedByDateIncrement,
    includeOtherDateRange,
    otherDataGroupedByDateIncrement,
    showPercentChange
  ]);

  const comparisonDropdowns = useMemo(
    () =>
      comparisonMetrics.map((metric, index) => (
        <div className='comparison-dropdown-item' key={`comparisonDropdown${metric}`}>
          <div className='cm-vs-label'>vs</div>
          {/* Not allowed to remove the first comparison metric. Always have to be comparing at least 2 metrics to each other. */}
          {index !== 0 && (
            <InteractiveDiv
              className='remove-button'
              onClick={() => setComparisonMetrics(prev => prev.filter((_metric, i) => i !== index))}
            >
              <MdClose />
            </InteractiveDiv>
          )}
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            key={index}
            type={DropdownToggleType.WIDGET_TITLE}
            value={metric}
            options={R.sort((a, b) => a.label.localeCompare(b.label), metricOptions)}
            onChange={option =>
              setComparisonMetrics(prev => prev.map((m, i) => (i === index ? option : m)))
            }
          />
        </div>
      )),
    [comparisonMetrics, metricOptions]
  );

  const { bars, lines, yAxes, legend } = useMemo(
    () =>
      getCompareMetricsCartesianComponents(
        primaryMetric,
        comparisonMetrics,
        dates,
        otherDates,
        includeOtherDateRange,
        showPercentChange,
        hoveredLegendItem,
        setHoveredLegendItem,
        focusedMetric,
        setFocusedMetric,
        metricsToPrettyNameMap,
        currSymbol
      ),
    [
      primaryMetric,
      comparisonMetrics,
      includeOtherDateRange,
      dates,
      otherDates,
      showPercentChange,
      hoveredLegendItem,
      setFocusedMetric,
      focusedMetric,
      metricsToPrettyNameMap,
      currSymbol
    ]
  );

  const [headers, superHeaders] = useMemo(() => {
    let computedSuperHeaders;
    const computedHeaders: TypedHeader<JoinedData>[] = [
      {
        label: 'Date',
        name: 'date',
        width: 60,
        nonInteractive: true,
        renderer: (item: JoinedData) => formatDateLabel(item.date, fiscalCal, '', true) || '-'
      },
      {
        label: metricsToPrettyNameMap[primaryMetric],
        name: primaryMetric,
        flex: 1,
        nonInteractive: true,
        renderer: (item: JoinedData) =>
          R.path(['metrics', primaryMetric], item) === '-'
            ? '-'
            : metricFormatter(showPercentChange ? 'relative' : primaryMetric)(
                R.pathOr(0, ['metrics', primaryMetric], item)
              ) || '-'
      }
    ];

    for (const metric of comparisonMetrics) {
      computedHeaders.push({
        label: metricsToPrettyNameMap[metric],
        name: metric,
        flex: 1,
        nonInteractive: true,
        renderer: (item: JoinedData) =>
          R.path(['metrics', metric], item) === '-'
            ? '-'
            : metricFormatter(showPercentChange ? 'relative' : metric)(
                R.pathOr(0, ['metrics', metric], item)
              ) || '-'
      });
    }

    if (includeOtherDateRange && !showPercentChange) {
      computedHeaders.push({
        label: metricsToPrettyNameMap[primaryMetric],
        name: primaryMetric,
        flex: 1,
        nonInteractive: true,
        renderer: (item: JoinedData) =>
          metricFormatter(primaryMetric)(R.pathOr(0, ['metrics_other', primaryMetric], item)) || '-'
      });

      for (const metric of comparisonMetrics) {
        computedHeaders.push({
          label: metricsToPrettyNameMap[metric],
          name: metric,
          flex: 1,
          nonInteractive: true,
          renderer: (item: JoinedData) =>
            metricFormatter(metric)(R.pathOr(0, ['metrics_other', metric], item)) || '-'
        });
      }

      computedHeaders.push({
        label: 'Date',
        name: 'date_other',
        width: 60,
        nonInteractive: true,
        renderer: (item: JoinedData) => formatDateLabel(item.date_other, fiscalCal, '', true) || '-'
      });

      const superHeaderSpan = comparisonMetrics.length + 2; // +1 for date, +1 for primaryMetric

      computedSuperHeaders = [
        { span: superHeaderSpan, data: 'Primary Period' },
        { span: superHeaderSpan, data: 'Comparison Period' }
      ];
    } else if (showPercentChange) {
      computedHeaders.push({
        label: 'Date',
        name: 'date_other',
        width: 60,
        nonInteractive: true,
        renderer: (item: JoinedData) => formatDateLabel(item.date_other, fiscalCal, '', true) || '-'
      });

      const superHeaderSpan = comparisonMetrics.length + 1; // +1 for primaryMetric

      computedSuperHeaders = [
        { span: 1, data: 'Primary Period' },
        {
          span: superHeaderSpan,
          data: 'Percent change from Comparison Period to Primary Period'
        },
        { span: 1, data: 'Comparison Period' }
      ];
    }
    return [computedHeaders, computedSuperHeaders];
  }, [
    metricsToPrettyNameMap,
    primaryMetric,
    includeOtherDateRange,
    comparisonMetrics,
    showPercentChange,
    metricFormatter,
    fiscalCal
  ]);
  const fileNameIdentifiers = useMemo(() => {
    const date =
      DATE_GROUPING_OPTIONS.find(item => item.value === groupingKey)?.label || groupingKey;
    const metricPrettyNames = comparisonMetrics.map(
      metric => metricOptions.find(item => item.value === metric)?.label || metric
    );
    const primaryMetricsPrettyName =
      metricOptions.find(item => item.value === primaryMetric)?.label || primaryMetric;

    const fileNameItems = [
      ...pngFileIdentifiers,
      date,
      primaryMetricsPrettyName,
      ...metricPrettyNames,
      `${dates.start}–${dates.end}`
    ];
    if (includeOtherDateRange) {
      fileNameItems.push(`vs_${otherDates.start}–${otherDates.end}`);
    }
    return fileNameItems;
  }, [
    groupingKey,
    comparisonMetrics,
    pngFileIdentifiers,
    dates.start,
    dates.end,
    includeOtherDateRange,
    metricOptions,
    otherDates.start,
    otherDates.end,
    primaryMetric
  ]);

  const excelDownload = useCallback(() => {
    const exportData = includeOtherDateRange
      ? processComparisonExport(dataGroupedByDateIncrement, otherDataGroupedByDateIncrement)
      : processExport(dataGroupedByDateIncrement);

    exportMultiSheetToExcel(
      [
        { name: 'Compare Metrics', data: exportData },
        { name: 'Data Info', data: dataInfo }
      ],
      fileNameIdentifiers.join('_')
    );
  }, [
    fileNameIdentifiers,
    dataGroupedByDateIncrement,
    otherDataGroupedByDateIncrement,
    includeOtherDateRange,
    dataInfo
  ]);

  const pngDownload = useCallback(async () => {
    await downloadPNG('.compare-metrics-container', fileNameIdentifiers, pngDownloadLabel, [
      '.right-side'
    ]);
  }, [pngDownloadLabel, fileNameIdentifiers]);

  let xAxisDataKey: 'date' | 'date_other' = 'date';
  if (defaultIncludeOtherDates && R.length(data) + 1 < R.length(otherData)) {
    xAxisDataKey = 'date_other';
  }

  let chartContainerChildren: ReactNode;
  if (awaitingServerData) {
    chartContainerChildren = (
      <div className='cm-no-data'>
        <Spinner />
      </div>
    );
  } else if (R.isEmpty(joinedData)) {
    chartContainerChildren = <div className='cm-no-data'>No data to show</div>;
  } else if (graphView) {
    chartContainerChildren = (
      <div className='cm-graph'>
        {comparisonMetrics.length <= 1 && !showPercentChange && (
          <div className='cm-above-graph'>
            <div className='cm-left-title'>
              {metricsToPrettyNameMap[primaryMetric]
                ? metricsToPrettyNameMap[primaryMetric]
                : getPrettyMetricName(primaryMetric)}
            </div>
            <div className='cm-right-title'>
              {metricsToPrettyNameMap[comparisonMetrics[0]]
                ? metricsToPrettyNameMap[comparisonMetrics[0]]
                : getPrettyMetricName(comparisonMetrics[0])}
            </div>
          </div>
        )}
        <div className='cm-graph-contents'>
          <div id='cm-graph' className='cm-graph'>
            <AutoSizer
              renderProp={({ width, height }) => (
                <ComposedChart data={joinedData} width={width} height={height}>
                  <CartesianGrid
                    vertical={false}
                    stroke={CARTESIAN_GRID_STROKE}
                    strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                  />
                  <Tooltip
                    content={
                      <CompareMetricsTooltip
                        includeOtherDateRange={includeOtherDateRange}
                        showPercentChange={showPercentChange}
                        fiscalCal={fiscalCal}
                        groupingKey={groupingKey}
                        metricFormatter={metricFormatter}
                      />
                    }
                    isAnimationActive={false}
                  />
                  <XAxis
                    dataKey={xAxisDataKey}
                    padding={X_AXIS_PADDING}
                    tick={TICK_STYLE}
                    tickFormatter={date => formatDateLabel(date, fiscalCal, groupingKey)}
                    axisLine={false}
                  />
                  {yAxes}
                  {bars}
                  {lines}
                </ComposedChart>
              )}
            />
          </div>
          <div className='right-of-chart'>{legend}</div>
        </div>
      </div>
    );
  } else {
    chartContainerChildren = (
      <BPMTable
        superHeaders={superHeaders}
        headers={headers}
        data={joinedData}
        filterBar={false}
        superHeaderHeight={30}
        headerHeight={30}
      />
    );
  }

  return (
    <div className={`compare-metrics-container ${showPercentChange ? 'show-percent-change' : ''}`}>
      <ChartContainer
        className='cc-row-chart'
        title={
          <>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              type={DropdownToggleType.WIDGET_TITLE}
              value={groupingKey}
              options={dateGroupingOptions} // TODO: calculate date grouping options to show
              onChange={option => setGroupingKey(option)}
            />
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              type={DropdownToggleType.WIDGET_TITLE}
              value={primaryMetric}
              options={R.sort((a, b) => a.label.localeCompare(b.label), metricOptions)}
              onChange={option => setPrimaryMetric(option)}
            />
            {comparisonDropdowns}
            {comparisonDropdowns.length < MAX_COMPARISON_METRICS && (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                className='add-comparison-metric-dropdown'
                stretch={StretchMode.FIT}
                hideCaret
                placeholder='+'
                options={R.sort(
                  (a, b) => a.label.localeCompare(b.label),
                  (
                    metricOptions as {
                      label: string;
                      value: Metric;
                    }[]
                  ).filter(option => ![...comparisonMetrics, primaryMetric].includes(option.value))
                )}
                onChange={(option: Metric) => setComparisonMetrics(prev => [...prev, option])}
              />
            )}
            <DateRangeComparisonTitle
              dates={dates}
              otherDates={otherDates}
              fiscalCal={fiscalCal}
              includeOtherDateRange={includeOtherDateRange}
              onShowComparison={() => setIncludeOtherDateRange(true)}
              onRemoveComparison={() => {
                setIncludeOtherDateRange(false);
                setShowPercentChange(false);
              }}
            />
          </>
        }
        rightActions={
          <>
            {includeOtherDateRange && (
              <ToggleSwitch
                label='% Change'
                checked={showPercentChange}
                onChange={setShowPercentChange}
              />
            )}
            <GraphTableToggleButton
              graphView={graphView}
              setGraphView={setGraphView}
              widgetName='compare_metrics'
              size={Size.MD}
            />
            <DownloadDropdown
              onClickOptions={[excelDownload, pngDownload]}
              disabledMenuOptions={
                graphView ? { XLSX: false, PNG: false } : { XLSX: false, PNG: true }
              }
            />
          </>
        }
        footerContent={includeFooter ? footerContent : undefined}
      >
        {chartContainerChildren}
      </ChartContainer>
    </div>
  );
};

const processExport = (data: MetricTotalsByDate[]) => {
  const response: Record<string, unknown>[] = [];
  for (const item of data) {
    const { date, metrics } = item;

    const prettyNameMetrics = {};
    for (const metric of R.keys(metrics)) {
      prettyNameMetrics[METRIC_TO_PRETTY_NAME[metric]] = metrics[metric];
    }

    response.push({
      Date: date,
      ...prettyNameMetrics
    });
  }

  return response;
};

const processComparisonExport = (data: MetricTotalsByDate[], otherData: MetricTotalsByDate[]) => {
  const unionData: ({ Period: number; Date: string } & Record<string, unknown>)[] = [];

  for (const item of data) {
    const { date, metrics } = item;

    const prettyNameMetrics = {};
    for (const metric of R.keys(metrics)) {
      prettyNameMetrics[METRIC_TO_PRETTY_NAME[metric]] = metrics[metric];
    }

    unionData.push({
      Period: 1,
      Date: date,
      ...prettyNameMetrics
    });
  }

  for (const item of otherData) {
    const { date, metrics } = item;

    const prettyNameMetrics = {};
    for (const metric of R.keys(metrics)) {
      prettyNameMetrics[METRIC_TO_PRETTY_NAME[metric]] = metrics[metric];
    }

    unionData.push({
      Period: 2,
      Date: date,
      ...prettyNameMetrics
    });
  }

  return unionData.sort((a, b) => {
    if (a.Period !== b.Period) {
      return a.Period - b.Period;
    }

    const dateA = new Date(a.Date);
    const dateB = new Date(b.Date);

    return dateA.getTime() - dateB.getTime();
  });
};
